import { useMemo } from 'react';

/**
 * Hook for calculating pricing-related values including discounts, surcharges, and payouts.
 *
 * IMPORTANT BUSINESS LOGIC:
 * - During soft launch, hosts keep 100% of the configured price.
 * - Then apply weekend surcharges and "Rabatte für längere Buchungen" (percentage or fixed €)
 *   on this payout base.
 * - basePrice is always **per hour** here (studio setup preview). Fixed € surcharge/discount values
 *   are therefore per hour; total booking totals multiply by hours in {@link calculateStudioPrice}.
 *
 * @param {number|string} basePrice - The base price per hour
 * @param {Object|null} discount - Discount object with discountType and discountValue
 * @param {Object|null} weekendSurcharge - Weekend surcharge object with type and value
 * @param {string} weekendMode - 'percentage' or 'amount' for weekend surcharge
 * @returns {Object} Object containing calculated values
 */
export const usePricingCalculations = (basePrice, discount = null, weekendSurcharge = null, weekendMode = 'percentage') => {
    const price = parseFloat(basePrice) || 0;
    const HOST_PAYOUT_MULTIPLIER = 1; // Soft launch: 0% host/platform fee

    const calculations = useMemo(() => {
        // 1) Calculate payout base after platform fee (currently none in soft launch)
        const payoutBase = price * HOST_PAYOUT_MULTIPLIER;

        // 2) Apply weekend surcharge on payout base
        let weekendSurchargeAmount = 0;
        if (weekendSurcharge && weekendSurcharge.value) {
            const surchargeValue = parseFloat(weekendSurcharge.value) || 0;
            if (weekendMode === "percentage") {
                weekendSurchargeAmount = payoutBase * (surchargeValue / 100);
            } else {
                // amount surcharge in €
                weekendSurchargeAmount = surchargeValue;
            }
        }

        const subtotalBeforeDiscount = payoutBase + weekendSurchargeAmount;

        // 3) Apply discount on payout subtotal (after surcharge)
        let discountAmount = 0;
        if (discount && discount.discountType && discount.discountValue) {
            const discountValue = parseFloat(discount.discountValue) || 0;
            if (discount.discountType === "percentage") {
                discountAmount = subtotalBeforeDiscount * (discountValue / 100);
            } else {
                // amount discount in € directly on payout
                discountAmount = discountValue;
            }
        }

        const finalPayout = Math.max(0, subtotalBeforeDiscount - discountAmount);

        return {
            basePrice: price,
            payoutBase,
            payoutBeforeDiscount: subtotalBeforeDiscount,
            payout: finalPayout,
            payoutRounded: Math.round(finalPayout),
            serviceFee: price - payoutBase,
            discountApplied: discount ? {
                type: discount.discountType,
                value: discount.discountValue,
                amount: discountAmount
            } : null,
            surchargeApplied: weekendSurcharge && weekendSurcharge.value ? {
                type: weekendMode,
                value: weekendSurcharge.value,
                amount: weekendSurchargeAmount
            } : null
        };
    }, [price, discount, weekendSurcharge, weekendMode]);

    return calculations;
};

/**
 * Pure helper (no React hooks) to calculate payout with optional weekend surcharge,
 * applying the current platform fee first and then the surcharge on the payout.
 *
 * Used in UI display where we cannot or do not want to use hooks.
 *
 * @param {number|string} basePrice
 * @param {{ value: string|number }|null} weekendSurcharge
 * @param {"percentage"|"amount"} weekendMode
 * @returns {{ payoutBase: number, payout: number, payoutRounded: number }}
 */
export const calculatePayoutWithWeekend = (basePrice, weekendSurcharge = null, weekendMode = 'percentage') => {
    const price = parseFloat(basePrice) || 0;
    const HOST_PAYOUT_MULTIPLIER = 1; // Soft launch: 0% host/platform fee

    const payoutBase = price * HOST_PAYOUT_MULTIPLIER;

    let weekendSurchargeAmount = 0;
    if (weekendSurcharge && weekendSurcharge.value) {
        const surchargeValue = parseFloat(weekendSurcharge.value) || 0;
        if (weekendMode === "percentage") {
            weekendSurchargeAmount = payoutBase * (surchargeValue / 100);
        } else {
            weekendSurchargeAmount = surchargeValue;
        }
    }

    const finalPayout = Math.max(0, payoutBase + weekendSurchargeAmount);

    return {
        payoutBase,
        payout: finalPayout,
        payoutRounded: Math.round(finalPayout)
    };
};

/**
 * Simple payout calculation function (for backward compatibility)
 * @param {number|string} basePrice - The base price per hour
 * @param {Object|null} discount - Discount object with discountType and discountValue
 * @returns {number} Payout amount after discount and platform fee
 */
export const calculatePayout = (basePrice, discount = null) => {
    const price = parseFloat(basePrice) || 0;
    const HOST_PAYOUT_MULTIPLIER = 1; // Soft launch: 0% host/platform fee
    if (!discount) return price * HOST_PAYOUT_MULTIPLIER;

    const discountValue = parseFloat(discount.discountValue) || 0;
    if (discount.discountType === "percentage") {
        const discounted = price * (1 - discountValue / 100);
        return discounted * HOST_PAYOUT_MULTIPLIER;
    } else {
        const discounted = price - discountValue;
        return Math.max(0, discounted) * HOST_PAYOUT_MULTIPLIER;
    }
};
